import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { HttpHeaderNames } from '@buck-e-icp/interfaces';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, throwError } from 'rxjs';

import { IdpService } from '../services';
import { AuthActions, selectActiveUserProfile } from '../store';

function shouldAddAuthHeader(req: HttpRequest<unknown>): boolean {
    // Avoid sending auth headers to external services such as Google Maps */
    if (req.method === 'JSONP' || !req.url.startsWith('/')) {
        return false;
    }
    if (req.url.startsWith('/idp')) {
        return req.url.startsWith('/idp/v1/clients');
    }
    return true;
}

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
    if (!shouldAddAuthHeader(req)) {
        return next(req);
    }

    const idpService = inject(IdpService);
    const store = inject(Store);
    return idpService.getCurrentSession().pipe(
        catchError(() => {
            return of(null);
        }),
        map((session) => session?.accessToken),
        concatLatestFrom(() => store.select(selectActiveUserProfile)),
        switchMap(([accessToken, activeProfile]) => {
            const afterRequest = (error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    if (error.status === HttpStatusCode.Unauthorized) {
                        // Idp session expired or  app Session deleted on server, log out
                        // ErrorService ignores these 401 errors so no error message will be shown.
                        store.dispatch(AuthActions.logout({ redirectUri: '/' }));
                    }
                }
                return throwError(() => error);
            };
            if (accessToken) {
                return next(
                    req.clone({
                        setHeaders: {
                            [HttpHeaderNames.AUTHORIZATION]: `Bearer ${accessToken}`,
                            ...(activeProfile && { [HttpHeaderNames.DASHBOARD_PROFILE]: activeProfile.id }),
                        },
                    }),
                ).pipe(catchError(afterRequest));
            }
            return next(req).pipe(catchError(afterRequest));
        }),
    );
};
